import { Box, FormControlLabel, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import type { FieldError } from '../../../../../hooks/use-fetch'
import type { TrackAnnualPlanSupportCostRequest } from '../../../../../models/requestObjects/AnnualPlanRequestObjects'
import { type SupportCost } from '../../../../../models/SupportCost'
import { Modal } from '../../../../customComponents/Modal'
import { TrackSupportCost } from '../../../../../services/AnnualPlanService'

interface Props {
  orgId: number
  annualPlanId: number
  supportCost: SupportCost
  onClose: () => void
  onSave: () => void
}

interface TrackSupportCostForm {
  utilized: boolean | null
  budgetUsed?: number
}

export function TrackSupportCostModal (props: Props): JSX.Element {
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [formValues, setFormValues] = useState<TrackSupportCostForm>({ utilized: props.supportCost.utilized, budgetUsed: props.supportCost.actualBudget })
  const handleSave = async (): Promise<void> => {
    const completedForm = { ...formValues }
    if (!validateForm(completedForm)) {
      return
    }

    if (completedForm.utilized == null) {
      toast.error('Please indicate whether the support cost was utilized.')
      return
    }
    await trackSupportCost(completedForm)
    props.onSave()
  }

  async function trackSupportCost (completedForm: TrackSupportCostForm): Promise<void> {
    if (completedForm.utilized == null) {
      toast.error('Please indicate whether the support cost was utilized.')
      return
    }
    const request: TrackAnnualPlanSupportCostRequest = {
      id: props.supportCost.id,
      actualBudget: completedForm.budgetUsed,
      annualPlanId: props.annualPlanId,
      orgId: props.orgId,
      planOrganizationTaskId: props.supportCost.planOrganizationTaskId,
      utilized: completedForm.utilized
    }

    const { success, errors } = await TrackSupportCost(request)
    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    props.onClose()
  }

  function validateForm (completedForm: TrackSupportCostForm): boolean {
    const notNumber: boolean = completedForm.budgetUsed != null && isNaN(Number(completedForm.budgetUsed.toString().replace(',', '')))
    if (notNumber) {
      toast.error('Budget must be a number greater than 0')
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    })

    setFieldErrors(fieldErrors.filter(f => f.fieldName !== e.target.name))
  }

  const content = <Box sx={{ width: '350px' }}>
    <Typography variant='h5' sx={{ mb: '1em' }}>Support Cost</Typography>
    <Typography variant='body1' sx={{ my: '1em' }}>Was this item utilized?</Typography>
    <RadioGroup
      row
      name="utilized"
      value={String(formValues.utilized)}
      onChange={handleFieldUpdate}
      aria-labelledby="demo-radio-buttons-group-label"
    >
      <FormControlLabel
        data-testid="utilized"
        value="true"
        control={<Radio />}
        label="Yes"
      />
      <FormControlLabel
        data-testid="notUtilized"
        value="false"
        control={<Radio />}
        label="No"
      />
    </RadioGroup>

    <Typography variant='h5' sx={{ mb: '1em' }}>Budget</Typography>
    <Typography variant='body1' sx={{ my: '1em' }}>How much funding was actually used? <Box component="span" sx={{ fontStyle: 'italic' }}>optional</Box></Typography>
    <TextField
      sx={{ width: '250px' }}
      variant='filled'
      onChange={handleFieldUpdate}
      label='Budget'
      data-testid='budgetUsed'
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      name={'budgetUsed'}
      value={formValues.budgetUsed}
    />

  </Box>

  return <Modal
    open={true}
    maxWidth='md'
    title='RCN Support Cost Tracking'
    confirmButtonText='Save'
    bodyContent={content}
    onClose={props.onClose}
    onConfirm={handleSave}
  />
}
